import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';

// Centralizes status → pill-style + default-label mapping (CONNECT 2.0 design
// system, Spec 2 Task 5). Renders the global `.pill`/`.pill--*` classes from
// `_components.scss` — this component owns no styling of its own, only the
// mapping. Replaces the status-label/status-class logic that today is
// scattered ad hoc across screens.
interface StatusPillConfig {
  modifier: string;
  label: string;
}

// Domain statuses this component knows about. Anything else falls back to a
// neutral pill (no crash, no default modifier color).
const STATUS_MAP: Record<string, StatusPillConfig> = {
  invitation: { modifier: 'pill--invitation', label: 'Quote Request' },
  pending: { modifier: 'pill--warning', label: 'Pending' },
  accepted: { modifier: 'pill--info', label: 'Accepted' },
  awarded: { modifier: 'pill--success', label: 'Awarded' },
  in_progress: { modifier: 'pill--active', label: 'In Progress' },
  declined: { modifier: 'pill--danger', label: 'Declined' },
  not_awarded: { modifier: 'pill--danger', label: 'Not Awarded' },
  paid: { modifier: 'pill--success', label: 'Paid' },
  invoiced: { modifier: 'pill--info', label: 'Invoiced' },
  on_hold: { modifier: 'pill--warning', label: 'On Hold' },
  // Comments card (Spec 5C, Task 3) — derived per-thread status.
  outstanding: { modifier: 'pill--warning', label: 'Outstanding' },
  resolved: { modifier: 'pill--success', label: 'Resolved' },
};

// Neutral fallback for an unknown/missing status — no modifier class, so it
// renders with only the base `.pill` (border/ink default) styling.
const FALLBACK: StatusPillConfig = { modifier: '', label: 'Unknown' };

@Component({
  standalone: true,
  selector: 'app-status-pill',
  imports: [CommonModule],
  templateUrl: './status-pill.component.html',
  styleUrl: './status-pill.component.scss',
})
export class StatusPillComponent {
  // Domain status key, e.g. 'in_progress', 'not_awarded'. Case/whitespace are
  // normalized before lookup so callers passing raw API values (any casing)
  // still resolve.
  @Input() status: string | null | undefined;

  // Optional override for the default human-readable label. When omitted,
  // the mapped (or fallback) label is shown.
  @Input() label?: string;

  // Optional QA hook (CONNECT 2.0 data-testid convention, Spec 2 Task 9).
  // Renders as `data-testid="<testid>"` on the pill's root element. Omitted
  // entirely when not set — never rendered empty/null. See
  // `src/app/shared/testid/README.md` for the naming convention.
  @Input() testid?: string;

  private get config(): StatusPillConfig {
    const key = (this.status ?? '').trim().toLowerCase();
    return STATUS_MAP[key] ?? FALLBACK;
  }

  get pillClass(): string {
    const modifier = this.config.modifier;
    return modifier ? `pill ${modifier}` : 'pill';
  }

  get displayLabel(): string {
    return this.label ?? this.config.label;
  }
}
